/** Pointer samples use the same 1600 by 1200 space on every screen. */
import { DRAWING_WIDTH, DRAWING_HEIGHT } from "./coordinates.js";
import { useRef, useCallback } from "preact/hooks";
import type { RefObject } from "preact";
import type { Point } from "@drawlol/shared";

/** Raw sample captured from a PointerEvent. */
export type PointerSample = {
  x: number; // drawing coordinates (0..1600)
  y: number; // drawing coordinates (0..1200)
  /** Raw PointerEvent.pressure [0,1]. 0 for mouse/touch. */
  pressure: number;
  /** Milliseconds since stroke-start. */
  dtMs: number;
};

export type StrokeCallbacks = {
  onStrokeStart: (p: Point, pointerType: string, pressure: number, pointerId: number) => void;
  onStrokeSample: (sample: PointerSample, pointerId: number) => void;
  onStrokeEnd: (pointerId: number) => void;
};

/** Map a screen point to the common drawing space. */
function toDrawingPoint(e: PointerEvent, canvas: HTMLCanvasElement): Point {
  const rect = canvas.getBoundingClientRect();
  return {
    x: (e.clientX - rect.left) * DRAWING_WIDTH / (rect.width || DRAWING_WIDTH),
    y: (e.clientY - rect.top) * DRAWING_HEIGHT / (rect.height || DRAWING_HEIGHT),
  };
}

/**
 * Returns the set of pointer-event handlers to spread onto a <canvas>.
 * Implements pen-precedence and isPrimary filtering.
 */
export function usePointerHandlers(
  canvasRef: RefObject<HTMLCanvasElement>,
  callbacks: StrokeCallbacks,
  enabled: boolean = true,
) {
  /** True once any pen event has been seen, suppresses touch after that. */
  const penSeenRef = useRef(false);
  /** Active stroke start times keyed by pointerId. */
  const strokeStartRef = useRef<Map<number, number>>(new Map());

  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (!enabled) return;
      if (e.pointerType === "mouse" && (e.button ?? 0) !== 0) return;
      if (!e.isPrimary) return;
      if (e.pointerType === "pen") penSeenRef.current = true;
      if (e.pointerType === "touch" && penSeenRef.current) return;

      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.setPointerCapture(e.pointerId);

      const now = performance.now();
      strokeStartRef.current.set(e.pointerId, now);

      const pt = toDrawingPoint(e, canvas);
      cbRef.current.onStrokeStart(pt, e.pointerType, e.pressure, e.pointerId);

      // Emit the first sample at dt=0
      cbRef.current.onStrokeSample(
        { x: pt.x, y: pt.y, pressure: e.pressure, dtMs: 0 },
        e.pointerId,
      );
    },
    [canvasRef, enabled],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!enabled) return;
      if (!e.isPrimary) return;
      if (e.pointerType === "touch" && penSeenRef.current) return;

      const canvas = canvasRef.current;
      if (!canvas) return;
      const startTime = strokeStartRef.current.get(e.pointerId);
      if (startTime === undefined) return; // no active stroke

      const pt = toDrawingPoint(e, canvas);
      const dtMs = performance.now() - startTime;
      cbRef.current.onStrokeSample({ x: pt.x, y: pt.y, pressure: e.pressure, dtMs }, e.pointerId);
    },
    [canvasRef, enabled],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (!enabled) return;
      if (!e.isPrimary) return;
      if (e.pointerType === "touch" && penSeenRef.current) return;

      strokeStartRef.current.delete(e.pointerId);
      cbRef.current.onStrokeEnd(e.pointerId);
    },
    [enabled],
  );

  const handlePointerCancel = useCallback(
    (e: PointerEvent) => {
      if (!enabled) return;
      if (!e.isPrimary) return;
      strokeStartRef.current.delete(e.pointerId);
      cbRef.current.onStrokeEnd(e.pointerId);
    },
    [enabled],
  );

  /**
   * n6: Handle lostpointercapture to clean up strokeStartRef entries.
   *
   * `pointercancel` fires for explicit cancellations (e.g. scroll interrupt),
   * but `lostpointercapture` fires when the browser revokes pointer capture
   * for any reason (tab switch, OS gesture, window focus loss). Without this
   * handler, strokeStartRef retains a dangling entry for the pointerId, which
   * prevents the next stroke from starting cleanly on some browsers.
   *
   * Per MDN: "lostpointercapture is fired after pointercancel in cases where
   * the browser cancels the capture", we guard with `has()` to avoid double
   * calling onStrokeEnd.
   */
  const handleLostPointerCapture = useCallback(
    (e: PointerEvent) => {
      if (!enabled) return;
      if (!strokeStartRef.current.has(e.pointerId)) return; // already ended
      strokeStartRef.current.delete(e.pointerId);
      cbRef.current.onStrokeEnd(e.pointerId);
    },
    [enabled],
  );

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onLostPointerCapture: handleLostPointerCapture,
  };
}
